// /src/core/me.ts
/**
 * Public-page identity helper (non-forcing).
 *
 * - 401 or failure => { authenticated:false }
 * - 200 => returns { authenticated:true, subject, name, email, raw }
 */

export type Me = {
  authenticated: boolean;
  userId?: string;
  email?: string;
  displayName?: string;
  firstName?: string;
  lastName?: string;
  claims?: Array<{ typ: string; val: string }>;
  [k: string]: any;
};

export async function getMeSafe(): Promise<Me> {
  try {
    const res = await fetch("/v1/me", {
      method: "GET",
      headers: { Accept: "application/json" },
      credentials: "include",
      redirect: "manual",
      cache: "no-store",
    });

    // When EasyAuth expires, /v1/me redirects to the IdP. With manual redirect,
    // browsers surface an opaqueredirect (status 0) instead of following it.
    if (res.type === "opaqueredirect") return { authenticated: false };

    // Handle same-origin redirects explicitly (should not happen often, but safe).
    if (res.status >= 300 && res.status < 400) return { authenticated: false };

    // a redirect is generated by a web site gate when:
    // 1. Vite is serving an html page without checking cookie but /v1/me hits the gate
    // 2. Cookie exists in Azure environment but user is rmoved from gate group
    // if (import.meta.env.DEV && res.redirected) <--- not only in dev but also in upper envs
    if (res.redirected) {
      if (res.ok) window.location.assign(res.url); // we follow if fetch followed
      // ensure callers don't keep rendering
      throw new Error("GATE_REDIRECT");
    }

    // 401 means not authenticated by Easy Auth. BFF is not the case because it's not required to call /v1/me
    if (res.status === 401) return { authenticated: false };
    // not sure in which cases we end up here with errors, probably it's just fail safely behavior
    if (!res.ok) return { authenticated: false };

    // we are good, parse json
    return (await res.json()) as Me;
  } catch {
    return { authenticated: false }; // this is where we end up in Azure environment when not authenticated - CORS failure
  }
}
